import { useCallback } from 'react';
import type { MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
import { selectHome, selectParkingLot } from 'app/pages/Home/slice/selectors';
import { saveUserPreferences } from 'utils/helpers';
import {
  buildEventHighlightSet,
  tbdMatchesHighlightSet
} from '../utils/buildEventHighlightSet';
import { clearEventFilterPreferences } from '../utils/clearEventFilterPreferences';
import {
  isMonthlyEventItem,
  resetMonthlyEventHighlightSourceKey
} from '../utils/monthlyEventHighlight';
import type { MonthlyTimelineItem } from '../utils/types';

export function useMonthlyEventDoubleClick(
  item: MonthlyTimelineItem,
  laneLabel: string
) {
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();
  const homeState = useSelector(selectHome);
  const showParkingLot = useSelector(selectParkingLot);

  const handleDoubleClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();

      if (!isMonthlyEventItem(item, laneLabel)) return;

      const issueId = item.issueId || '';
      if (!issueId) return;

      const isAlreadySelected =
        homeState.events?.selectedEvents?.includes(issueId);

      if (isAlreadySelected) {
        dispatch(actions.setSelectedEvents([]));
        dispatch(actions.clearHighlightedIssues());
        resetMonthlyEventHighlightSourceKey();
        saveUserPreferences({ events: [] });
        saveUserPreferences({ eventsLabel: [] });
        return;
      }

      dispatch(actions.clearGenAiFilters());
      dispatch(actions.setSelectedFeatures([]));
      dispatch(actions.setSelectedProducts([]));
      dispatch(actions.setSelectedCats([]));
      dispatch(actions.setSelectedGroups([]));
      dispatch(actions.setSelectedAudiences([]));
      dispatch(actions.clearCategoryProductSelection());
      dispatch(actions.clearRevenueImpactFilters());
      clearEventFilterPreferences();

      const { highlightSet, parentToUnnest } = buildEventHighlightSet(
        item,
        'filter'
      );

      resetMonthlyEventHighlightSourceKey();
      dispatch(actions.setSelectedEvents([issueId]));
      dispatch(
        actions.updateHighlightedIssue({
          relatedIssues: Array.from(highlightSet),
          unnestParentIds: Array.from(parentToUnnest),
          source: 'filter'
        })
      );

      const tbdData = homeState.releaseData?.tbd || [];
      if (!showParkingLot && tbdMatchesHighlightSet(tbdData, highlightSet)) {
        dispatch(actions.setParkingLot(true));
      }

      saveUserPreferences({ events: [issueId] });
    },
    [actions, dispatch, homeState, item, laneLabel, showParkingLot]
  );

  return { handleDoubleClick };
}
